<script setup>
import { ref, computed, watch } from 'vue';
import * as calc from '../calc.js';
import { useShared } from '../state.js';
import { usePersisted } from '../use_persisted.js';
import { scopedKey, useSetups } from '../setups.js';
import { useToasts } from '../use_toasts.js';
import { useScrollEdges } from '../use_scroll_edges.js';
import { useDeferredCompute } from '../use_deferred_compute.js';
import RecomputeButton from './recompute_button.vue';
import UnitSelect from './unit_select.vue';

const { inputs, computedResult, isStale, getLevelMarksTable, lookupLevelMark } = useShared();
const { pushToast } = useToasts();
const { activeSetupId } = useSetups();

const tableScrollWrapper = ref(null);
const { canScrollLeft, canScrollRight, update: updateScrollEdges } = useScrollEdges(tableScrollWrapper);

// Scoped to the active setup — see mark.vue for why.
const input = usePersisted(scopedKey('full-marks-inputs', activeSetupId.value), {
  unit: 'm',
  max_distance: 120,
});

let lastFullMarksError = null;

// Known synchronously, with no physics involved — how many rows the table
// will end up with, regardless of what's actually in them. Used to size
// the placeholder rows below (see .placeholder-row) to the real table's
// eventual height, rather than a generic "Computing…" line that's a
// fraction of it.
const rowCount = computed(() =>
  Math.min(Math.max(Number(input.value.max_distance) || 0, 3), 1000) - 1
);

// results/computing replace what used to be a plain computed() — a
// computed() getter can't yield mid-evaluation, and the whole point here
// is to break the loop into chunks (see yieldToFrame in the loop below)
// so it doesn't run as one uninterrupted block long enough to freeze the
// panel's own open animation. The watch below replicates the same
// reactive dependencies a computed() would have picked up automatically.
const results = ref([]);
const computing = ref(true);

async function computeRows() {
  computing.value = true;

  if (isStale.value) {
    console.log("calculated is stale, cannot compute full marks");
    results.value = [];
    computing.value = false;
    return;
  }

  let unit = input.value.unit;
  // Clamp so a stray huge/invalid value in the persisted max-distance
  // input can't hang the browser on thousands of solver iterations.
  let maxDistance = Math.min(Math.max(Number(input.value.max_distance) || 0, 3), 1000);

  const newResults = [];

  try {
    // Reads the shared level-ground table (see state.js) instead of
    // sweeping calculateMark itself — that table is built at a finer
    // resolution (0.25m) than this panel's own 1-unit rows, so each row
    // here is either an exact table value (whenever unit is 'm', since
    // every whole meter lands exactly on the table's 0.25m grid) or a
    // linear interpolation between two adjacent table rows a quarter-meter
    // apart (any other unit). Verified against a fresh calculateMark call
    // at the same distance: interpolation error stays within a couple
    // thousandths of a mark unit even at the shortest displayed
    // distances — the same "staircase" scale calc()'s own adaptive
    // integrator already introduces at fine distance scales (see
    // physics.rs's calc_core), so this isn't adding meaningfully more
    // noise than calling calculateMark directly already has at short range.
    const scale = calc.toScale(unit);
    const table = await getLevelMarksTable();

    for (let i = 2; i <= maxDistance; i += 1) {
      const row = lookupLevelMark(table, i * scale);
      if (!row) break; // past wherever the table's own physics stopped being viable

      const { mark: m, fps, t, drop, verticalMarks, horizontalMarks } = row;

      newResults.push({
        distance: i.toString() + unit,
        mark: calc.roundToClick(inputs.sight_settings, m),
        velocity: fps.toFixed(1) + " fps",
        time: t.toFixed(3) + " s",
        drop: drop.toFixed(1) + " cm/m",
        vertical: verticalMarks.toFixed(2) + " cm/turn",
        horizontal: horizontalMarks.toFixed(2) + " cm/turn",
      });
    }

    lastFullMarksError = null;

  } catch (error) {
    console.log('error computing marks:', error.message);
    console.log(error.stack);
    if (error.message !== lastFullMarksError) {
      lastFullMarksError = error.message;
      pushToast(`Full Marks: ${error.message}`);
    }
  }

  results.value = newResults;
  computing.value = false;
}

// The initial run is deferred (see use_deferred_compute.js) so the
// panel's own open animation gets to paint and start before this begins —
// a later recompute (the watch below, edited inputs/settings) isn't
// deferred since the panel is already open by then, with no animation
// racing it, but it's still chunked the same way so a big table doesn't
// freeze the page either way.
useDeferredCompute(() => computeRows());

watch([computedResult, input, () => inputs.sight_settings], () => {
  computeRows();
}, { deep: true });

const copied = ref(false);

function copyTable() {
  const headers = [
    'Distance',
    'Mark',
    'Velocity At Impact (fps)',
    'Time To Target (s)',
    'Drop (cm/m)',
    'Vertical (cm/turn)',
    'Horizontal (cm/turn)',
  ];

  const rows = results.value.map(m => [
    m.distance,
    m.mark,
    m.velocity,
    m.time,
    m.drop,
    m.vertical,
    m.horizontal,
  ]);

  const text = [headers, ...rows]
    .map(row => row.join('\t'))
    .join('\n');

  navigator.clipboard.writeText(text)
    .then(() => {
      copied.value = true;
      setTimeout(() => { copied.value = false; }, 2000);
    })
    .catch(e => console.warn('Failed to copy:', e));
}

</script>

<template>
  <div class="centered">
    <RecomputeButton />

    <div class="controls">
      <label>Unit:
        <UnitSelect v-model="input.unit" />
      </label>

      <label>Max distance:
        <input type="number" class="input-measure" v-model.number="input.max_distance" />
      </label>

      <button @click="copyTable" class="btn-copy">
        {{ copied ? 'Copied!' : 'Copy Table' }}
      </button>
    </div>

    <div class="table-scroll-outer">
      <div class="table-scroll-wrapper" ref="tableScrollWrapper">
        <table>
          <thead>
            <tr>
              <th>Distance</th>
              <th>Mark</th>
              <th>Velocity At Impact</th>
              <th>Time To Target</th>
              <th>Drop</th>
              <th>Vertical</th>
              <th>Horizontal</th>
            </tr>
          </thead>
          <!-- While computing, blank rows in the exact final row count
               (see rowCount above — known without touching any physics)
               hold the table at the same height calculateRows() will
               end up needing, so the panel doesn't animate open small
               and then jump bigger once the real rows replace these. -->
          <tbody v-if="computing">
            <tr v-for="n in rowCount" :key="n" class="placeholder-row">
              <td v-for="c in 7" :key="c">&nbsp;</td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="(mark, index) in results" :key="index">
              <td>{{mark.distance}}</td>
              <td>{{mark.mark}}</td>
              <td>{{mark.velocity}}</td>
              <td>{{mark.time}}</td>
              <td>{{mark.drop}}</td>
              <td>{{mark.vertical}}</td>
              <td>{{mark.horizontal}}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="scroll-shadow scroll-shadow-left" :class="{ visible: canScrollLeft }"></div>
      <div class="scroll-shadow scroll-shadow-right" :class="{ visible: canScrollRight }"></div>
    </div>
  </div>

</template>

<style scoped>

.controls {
  display: flex;
  gap: var(--space-lg);
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: var(--space-sm);
}

.placeholder-row {
  opacity: 0.4;
}

/* Positioning context for the scroll-edge shadows below — a plain wrapper
   around .table-scroll-wrapper so the shadows can sit as its SIBLINGS,
   painted above the table itself regardless of striping or sticky-cell
   backgrounds inside it (a background-image on the scrolling element
   itself, tried first, was defeated by the even-row stripe painting over
   it on every other row). */
.table-scroll-outer {
  position: relative;
  max-width: 100%;
}

.table-scroll-wrapper {
  /* `overflow-x: auto` alone forces the browser to also make this a
     vertical scroll container (per spec, an axis can't stay `visible`
     once the other is scrollable) — but with no height limit it never
     actually scrolls, so the sticky header has nothing to stick against.
     Bound the height and let it scroll both ways internally instead. */
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
  max-height: 70vh;
  border: var(--border);
  border-radius: var(--border-radius);
}

/* Horizontal-scroll hint: fades in at whichever edge still has more columns
   past it, and disappears once scrolled all the way to that side — see
   useScrollEdges (src/use_scroll_edges.js) for how canScrollLeft/Right are
   tracked. Absolutely positioned against .table-scroll-outer rather than as
   a background on the scrolling element itself so it always paints on top,
   regardless of what's happening inside (sticky header/column, row
   striping). */
.scroll-shadow {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 28px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 3;
}

.scroll-shadow.visible {
  opacity: 1;
}

.scroll-shadow-left {
  left: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.4), transparent);
}

.scroll-shadow-right {
  right: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.4), transparent);
}

table {
  white-space: nowrap;
  /* No min-width here — the table should only be as wide as its own
     (nowrap) cell content actually needs. A fixed floor would force
     horizontal scroll on a panel that could otherwise fit the real
     content, or waste space the panel doesn't have on a narrow one; plain
     content-sizing already gets both right on its own. */
  /* `border-collapse: collapse` (the global default) shares border paint
     between adjacent cells, which breaks around a sticky cell — a sliver
     of whatever's scrolling underneath shows through the seam. Separate
     borders paint each cell fully, so the sticky column stays opaque. */
  border-collapse: separate;
  border-spacing: 0;
}

th, td {
  text-align: center;
}

thead th {
  position: sticky;
  top: 0;
  background: var(--color-primary);
  color: var(--color-text-light);
  z-index: 1;
}

tbody tr:nth-child(even) {
  background: var(--color-bg-alt);
}

/* Sticky Distance column — stays visible while scrolling through the wide
   results table, so you can always tell which row you're looking at. */
th:first-child,
td:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.15);
  /* Pin the sticky cell to its own compositing layer so it stays a solid
     paint during fast/momentum scrolling instead of momentarily lagging
     behind and letting the scrolling content show through. */
  will-change: transform;
}

thead th:first-child {
  z-index: 2; /* top-left corner: above both the sticky header row and column */
}

td:first-child {
  background: var(--color-bg);
}

tbody tr:nth-child(even) td:first-child {
  background: var(--color-bg-alt);
}
</style>
